const Document = require('./document');
const { parseCulture, validateLanguage, validateLocale } = require('./culture');
const Visit = require('./visit');
const Url = require('./url');
const AbTestCookieManager = require('@vp/ab-test-cookie');
const libraryInfo = require('./libraryInfo');
const pageViewId = require('./pageViewId');
const cookieUtil = require('./cookieUtil');
const { v4: uuidv4 } = require('@lukeed/uuid');
const { vcsTenants } = require('./constants');
const browser = require('./browser');

class PropertyBuilder {
    constructor(properties, culture, tenant) {
        this.properties = properties || {};
        this.culture = culture;
        this.tenant = tenant;
    }

    static create(properties, culture, tenant) {
        return new PropertyBuilder(properties, culture, tenant);
    }

    getProperties() {
        return this.properties;
    }

    addVisit() {
        this.properties.visit = Visit.ensureVisit();

        return this;
    }

    addPtid() {
        this.properties.ptid = uuidv4();
        return this;
    }

    addPageViewId() {
        this.properties.pageViewId = pageViewId.getPageViewId();
        return this;
    }

    addChannel() {
        if (window.location) {
            const url = window.location.toString();
            const params = Url.getParameters(url, ['tid', 'partner', 'utm_id']);

            if (params.tid) {
                this.properties.tid = params.tid;
            }

            if (params.partner) {
                this.properties.partner = params.partner;
            }

            if (params.utm_id) {
                this.properties.utm_id = params.utm_id;
            }
        }

        return this;
    }

    addMetatagValues(names) {
        const keyValues = Document.getMetaTagValues(names);
        if (keyValues) {
            keyValues.forEach((kv) => {
                if (!this.properties[kv.key]) {
                    this.properties[kv.key] = kv.value;
                }
            });
        }

        return this;
    }

    addLanguageLocale(eventType, eventName = undefined) {
        const logError = (property, value) => {
            const eventDetails = eventName ? `'${eventName}' of type '${eventType}'` : `of type '${eventType}'`;

            console.error(`A tracking property '${property}' has invalid value '${value}' of an event ${eventDetails}`);
        };

        const language = this.properties.language?.toLowerCase();

        if (language) {
            this.properties.language = language;
        }

        if (language && !validateLanguage(language)) {
            logError('language', language);
            this.properties.language = undefined;
        }

        const locale = this.properties.locale?.toUpperCase();

        if (locale) {
            this.properties.locale = locale;
        }

        if (locale && !validateLocale(locale)) {
            logError('locale', locale);
            this.properties.locale = undefined;
        }

        const langLocale = parseCulture(this.culture);
        if (langLocale.language && !this.properties.language) {
            this.properties.language = langLocale.language;
        }

        if (langLocale.locale && !this.properties.locale) {
            this.properties.locale = langLocale.locale;
        }

        return this;
    }

    addTrackingInfo() {
        this.properties.trackingVersion = libraryInfo.version;

        if (this.tenant) {
            this.properties.trackingTenant = this.tenant;
        }

        return this;
    }

    addFiscalYear(date) {
        const month = date.getUTCMonth(); //ACHTUNG! Zero-based month!
        const year = date.getUTCFullYear();

        this.properties.fiscalYear = 'FY' + ((month > 5 ? year + 1 : year) % 100);

        return this;
    }

    addTestUserId() {
        this.properties.testUserId = AbTestCookieManager.ensureUserTestId();

        return this;
    }

    addTenantSpecificProperties() {
        if (vcsTenants.has(this.tenant)) {
            const storeId = Document.getMetaTagValue('storeId') || Document.getMetaTagValue('subtenant');

            if (storeId) {
                this.properties.storeId = storeId;
            }

            const subtenant = Document.getMetaTagValue('subtenant');
            if (subtenant) {
                this.properties.subtenant = subtenant;
            }
            return this;
        }
        return this;
    }

    addBotProperties() {
        const cfBotScore = Document.getMetaTagValue('vp-bot-score') || cookieUtil.getCookie('vp-bot-score');
        if (cfBotScore && !isNaN(cfBotScore)) this.properties.cf_bot_score = Number(cfBotScore);

        const cfBotVerified = Document.getMetaTagValue('vp-bot-verified') || cookieUtil.getCookie('vp-bot-verified');
        if (cfBotVerified) this.properties.cf_verified_bot = cfBotVerified.toLowerCase() === 'true';

        const cfBotCategory = Document.getMetaTagValue('vp-bot-category') || cookieUtil.getCookie('vp-bot-category');
        if (cfBotCategory && cfBotCategory.toLowerCase() != 'undefined')
            this.properties.cf_verified_bot_category = cfBotCategory;

        return this;
    }

    addCfVpjProperty() {
        const cfVpj = Document.getMetaTagValue('vpj');
        if (cfVpj) this.properties.cf_vpj = cfVpj.toLowerCase();

        return this;
    }

    addCfJdpProperty() {
        const cfJdp = cookieUtil.getCookie('vista-jdp');
        if (cfJdp) this.properties.cf_jdp = cfJdp.toLowerCase() === 'true';

        return this;
    }

    addCfLocaleProperties() {
        const cfIpCountry = cookieUtil.getCookie('cf-ipcountry');
        if (cfIpCountry) this.properties.cf_ip_country = cfIpCountry.toUpperCase();

        const cfRegionCode = cookieUtil.getCookie('cf-region-code');
        if (cfRegionCode) this.properties.cf_region_code = cfRegionCode.toUpperCase();

        const cfCity = cookieUtil.getCookie('cf-city');
        if (cfCity) this.properties.cf_city = cfCity;

        return this;
    }
    addViewportDimensions() {
        const dimensions = browser.getViewPortDimensions();
        if (dimensions && dimensions.width && dimensions.height) {
            this.properties.viewport_width = dimensions.width;
            this.properties.viewport_height = dimensions.height;
        }
        return this;
    }
}

module.exports = PropertyBuilder;
